import { largestContainerIssue, largestContainerRequired } from './largest-container.ts';
import { entryExtraValidation } from './report-extra-validation.ts';
import { facilityIdentityIssues, reportQuantityIssues, storageAmountIssue } from './report-field-validation.ts';
import { stateSupplementalIssues } from './state-supplemental-fields.ts';
import { contactHasFirstAndLastName } from './contact-name.ts';
import { isValidEmail } from './email.ts';
import { inventoryAmountIssues } from './inventory-amounts.ts';
import { hasSelectedVesselType } from './inventory-vessel.ts';
import { isValidPhone } from './phone.ts';
import { casIsValid } from '../../tri-shared/services/cas.ts';
import { isNaics2022Code } from '../../tri-shared/services/naics.ts';
import { exportRangeCode, reportHazardActive, TIER2_SUBMIT_HAZARD_CATEGORIES } from './tier2-codes.ts';
import { resolveChemicalStateFields, stateFieldIssues } from './state-fields.ts';
import { facilityStateIssues } from './jurisdiction-requirements.ts';
import type { TierIIReportFormEntry } from '../types/reporting.ts';
import type { FacilityMeta, FacilityMetaAddress } from './tier2submit-export.ts';

/**
 * The single definition of a complete Tier II report. The Report Form's progress, the backend's
 * gate on marking the report form complete, and the Tier2 Submit export preflight all read these
 * checks, so a report the UI shows as complete is one the export accepts.
 */

/** One requirement of the report; it is complete when it has no issues. */
export interface TierIIReportCheck {
  label: string;
  issues: string[];
}

export function tierIIReportChecksIncomplete(checks: TierIIReportCheck[]): TierIIReportCheck[] {
  return checks.filter((check) => check.issues.length > 0);
}

function hasCompleteAddress(address: FacilityMetaAddress | null): boolean {
  return Boolean(
    address?.street.trim() &&
    address.city.trim() &&
    address.state.trim() &&
    address.zip.trim() &&
    address.country.trim(),
  );
}

export function reportEntryLabel(entry: TierIIReportFormEntry): string {
  return entry.basis === 'compound' ? entry.chemicalName : entry.productName;
}

/** Facility identification, details, contacts, and certification. */
export function tierIIFacilityChecks(facility: FacilityMeta): TierIIReportCheck[] {
  const identification: string[] = [];
  if (!facility.name.trim()) identification.push('Facility name is required.');
  if (!facility.address.trim()) identification.push('Facility street address is required.');
  if (!facility.city.trim()) identification.push('Facility city is required.');
  if (!facility.county.trim()) identification.push('Facility county is required.');
  if (!facility.state.trim()) identification.push('Facility state is required.');
  if (!facility.zip.trim()) identification.push('Facility ZIP code is required.');
  const naicsCodes = facility.naicsCodes.map((code) => code.trim()).filter(Boolean);
  if (naicsCodes.length === 0) identification.push('At least one current 2022 NAICS code is required.');
  for (const code of naicsCodes) {
    if (!isNaics2022Code(code)) identification.push(`${code} is not a valid 2022 NAICS code.`);
  }

  const details: string[] = [];
  if (
    facility.latitude == null ||
    !Number.isFinite(facility.latitude) ||
    facility.latitude < -90 ||
    facility.latitude > 90
  ) {
    details.push('A valid facility latitude is required.');
  }
  if (
    facility.longitude == null ||
    !Number.isFinite(facility.longitude) ||
    facility.longitude < -180 ||
    facility.longitude > 180
  ) {
    details.push('A valid facility longitude is required.');
  }
  if (!isValidPhone(facility.emergencyPhone)) details.push('A valid facility 24-hour emergency phone is required.');
  if (facility.occupancyStatus !== 'manned' && facility.occupancyStatus !== 'unmanned') {
    details.push('Choose whether the facility is manned or unmanned.');
  }
  if (facility.maxOccupants == null || !Number.isInteger(facility.maxOccupants) || facility.maxOccupants < 0) {
    details.push('A valid maximum number of occupants is required.');
  }
  if (facility.subjectToSection302 == null) details.push('Answer the EPCRA Section 302 question.');
  if (facility.subjectToSection112r == null) details.push('Answer the CAA Section 112(r) question.');
  if (facility.subjectToSection112r === true && !facility.rmpFacilityID.trim()) {
    details.push('An RMP facility ID is required when the facility is subject to CAA Section 112(r).');
  }
  details.push(...facilityIdentityIssues(facility));

  const contacts = facility.contacts;
  const contactIssues: string[] = [];
  if (
    !contacts.some(
      (contact) =>
        contact.name.trim() &&
        isValidEmail(contact.email) &&
        contact.contactTypes.includes('Owner / Operator') &&
        contact.phones.some((phone) => isValidPhone(phone.number)) &&
        hasCompleteAddress(contact.mailingAddress),
    )
  ) {
    contactIssues.push(
      'An owner/operator contact with a valid phone, email, and complete mailing address is required.',
    );
  }
  if (
    !contacts.some(
      (contact) =>
        contactHasFirstAndLastName(contact) &&
        contact.title?.trim() &&
        isValidEmail(contact.email) &&
        contact.contactTypes.includes('Tier II Information Contact') &&
        contact.phones.some((phone) => isValidPhone(phone.number)),
    )
  ) {
    contactIssues.push('A Tier II information contact with a title, valid phone, and email is required.');
  }
  if (
    !contacts.some(
      (contact) =>
        contactHasFirstAndLastName(contact) &&
        contact.contactTypes.includes('Emergency Contact') &&
        contact.phones.some((phone) => phone.type === '24-hour' && isValidPhone(phone.number)),
    )
  ) {
    contactIssues.push('An emergency contact with a valid 24-hour phone is required.');
  }
  if (
    facility.subjectToSection302 === true &&
    !contacts.some(
      (contact) => contactHasFirstAndLastName(contact) && contact.contactTypes.includes('Fac. Emergency Coordinator'),
    )
  ) {
    contactIssues.push('A facility emergency coordinator with a first and last name is required.');
  }
  for (const contact of contacts) {
    if (!contact.contactTypes.some((type) => type === 'Emergency Contact' || type === 'Fac. Emergency Coordinator'))
      continue;
    const name = contact.name.trim();
    if (!name && !contact.title?.trim() && !contact.email?.trim() && contact.phones.length === 0) {
      contactIssues.push('An emergency contact is blank; complete or remove it.');
      continue;
    }
    const who = name || 'An unnamed emergency contact';
    if (!contactHasFirstAndLastName(contact)) contactIssues.push(who + ': enter a first and last name.');
    if (contact.phones.filter((phone) => isValidPhone(phone.number)).length < 2)
      contactIssues.push(who + ': provide both a daytime phone and a 24-hour phone.');
    // Email is optional, but a malformed one would be filed as-is.
    if (contact.email?.trim() && !isValidEmail(contact.email))
      contactIssues.push(who + ': enter a valid email address.');
  }

  const certification: string[] = [];
  if (!facility.certifierName.trim() || !facility.certifierTitle.trim()) {
    certification.push('The certifier name and title are required.');
  }
  if (!facility.dateSigned?.trim()) certification.push('The certification date is required.');

  return [
    { label: 'Identification', issues: identification },
    { label: 'Facility details', issues: details },
    { label: 'Contacts', issues: contactIssues },
    { label: 'Certification', issues: certification },
  ];
}

/** Facility-level and per-entry state reporting fields and attachments. */
export function tierIIStateIssues(facility: FacilityMeta, entries: TierIIReportFormEntry[]): string[] {
  return [
    ...facilityStateIssues(facility),
    ...stateSupplementalIssues(facility, entries),
    ...entries.flatMap((entry) =>
      stateFieldIssues(resolveChemicalStateFields(facility, entry), facility.state, 'chemical').map(
        (issue) => reportEntryLabel(entry) + ': ' + issue,
      ),
    ),
  ];
}

/** Amounts are computed in Inventory, so these issues can only be fixed there, not on the Report Form. */
export function tierIIEntryAmountIssues(entry: TierIIReportFormEntry): string[] {
  const issues = [
    ...inventoryAmountIssues(entry.maxAmountLbs, entry.avgAmountLbs),
    ...reportQuantityIssues(entry.maxAmountLbs, entry.avgAmountLbs),
  ];
  if (entry.basis === 'mixture') {
    if (
      entry.maxAmountLbs !== null &&
      [...entry.ehsChemicals, ...entry.nonEhsChemicals].some(
        (component) =>
          component.maxAmountLbs !== null &&
          Number(exportRangeCode(component.maxAmountLbs)) > Number(exportRangeCode(entry.maxAmountLbs)),
      )
    )
      issues.push('Component maximum amount range cannot exceed the mixture maximum amount range.');
    for (const component of entry.ehsChemicals)
      if (!exportRangeCode(component.maxAmountLbs))
        issues.push(`Maximum amount range is missing for EHS component ${component.name}.`);
  }
  return issues;
}

/** The hazard, amount, and storage details of one reportable chemical or product. */
export function tierIIEntryChecks(
  facility: Pick<FacilityMeta, 'state' | 'reportingYear'>,
  entry: TierIIReportFormEntry,
): TierIIReportCheck[] {
  const label = reportEntryLabel(entry);
  const checks: TierIIReportCheck[] = [];
  const identifiers = [
    ...(label.trim() ? [] : ['Chemical name is required.']),
    ...entryExtraValidation(facility, entry).errors,
  ];
  if (identifiers.length) checks.push({ label: 'Chemical identifiers', issues: identifiers });
  // Qualifier reviews happen on Threshold Review, so they only appear here while unresolved.
  if (entry.ehsQualifierIssues?.length)
    checks.push({ label: 'EHS qualifier review', issues: entry.ehsQualifierIssues });

  checks.push({
    label: 'Physical state',
    issues: entry.physicalStates.length ? [] : [`${label}: physical state is required.`],
  });

  // The primary reporting CAS is optional, so it only counts as a check once it is invalid.
  if (entry.basis === 'mixture' && entry.primaryReportingCas !== null && !casIsValid(entry.primaryReportingCas))
    checks.push({
      label: 'Primary reporting CAS',
      issues: [`${label}: primary reporting CAS must be a valid CAS number.`],
    });

  const hazards: string[] = [];
  if (!TIER2_SUBMIT_HAZARD_CATEGORIES.some((category) => category.keys.some((key) => reportHazardActive(entry, key))))
    hazards.push(`${label}: at least one hazard category is required.`);
  if (entry.hazHnoc && !entry.hazardNotes.trim())
    hazards.push(`${label}: describe the specific Hazard Not Otherwise Classified hazard.`);
  checks.push({ label: 'Hazards', issues: hazards });

  checks.push({
    label: 'Inventory amounts',
    issues: tierIIEntryAmountIssues(entry).map((issue) => `${label}: ${issue} Review the amounts in Inventory.`),
  });

  checks.push({
    label: 'Days on site',
    issues:
      entry.daysOnSite == null
        ? [`${label}: days on site is required.`]
        : !Number.isInteger(entry.daysOnSite) || entry.daysOnSite < 1 || entry.daysOnSite > 365
          ? [`${label}: days on site must be a whole number between 1 and 365.`]
          : [],
  });

  // Only states that require the largest container amount count it, unless a value was entered anyway.
  const containerRequired = largestContainerRequired(facility.state, facility.reportingYear);
  if (containerRequired || entry.maxAmountInLargestContainerLbs != null) {
    const containerIssue = largestContainerIssue(
      entry.maxAmountInLargestContainerLbs,
      entry.maxAmountLbs,
      containerRequired,
    );
    checks.push({
      label: 'Maximum amount in largest container',
      issues: containerIssue ? [`${label}: ${containerIssue}`] : [],
    });
  }

  const storage: string[] = [];
  if (entry.vessels.length === 0) storage.push(`${label}: add at least one storage location.`);
  if (entry.vessels.some((vessel) => vessel.vesselType === 'other' && !vessel.vesselTypeName?.trim()))
    storage.push(`${label}: name each custom vessel type.`);
  else if (entry.vessels.some((vessel) => !hasSelectedVesselType(vessel)))
    storage.push(`${label}: select a vessel type for every storage location.`);
  if (
    entry.vessels.some(
      (vessel) =>
        vessel.storageLocationMissing ||
        !vessel.storageLocation.trim() ||
        !vessel.storageType ||
        !vessel.pressure ||
        !vessel.temperature,
    )
  )
    storage.push(`${label}: select a location, storage type, pressure, and temperature for each storage entry.`);
  if (
    entry.vessels.some(
      (vessel) => vessel.maxAmountLbs != null && (!Number.isFinite(vessel.maxAmountLbs) || vessel.maxAmountLbs < 0),
    )
  )
    storage.push(`${label}: storage location amounts must be nonnegative numbers.`);
  for (const vessel of entry.vessels) {
    const issue = storageAmountIssue(vessel.maxAmountLbs);
    if (issue) storage.push(`${label}: ${issue}`);
  }
  if (entry.vessels.some((vessel) => vessel.amountFromInventory && vessel.maxAmountLbs == null))
    storage.push(`${label}: complete missing vessel amounts in Inventory.`);
  checks.push({ label: 'Storage details', issues: storage });

  return checks;
}
